import React, { useState } from 'react';
import { action, runInAction } from 'mobx';
import { useStore } from '@vromo/mobx-react';
import { observer } from 'mobx-react-lite';

import TrackingLinkPreview from './TrackingLinkPreview';
import { Campaign } from '@vromo/models';
import { RootStore } from '@vromo/dashboard/src/stores/RootStore';

import { serialize, deserialize, update } from 'serializr';

import Title from '@vromo/ui/components/Title';
import Modal, { ModalHeader, ModalActions } from '@vromo/ui/components/Modal';
import { Tabs, Tab } from '@vromo/ui/components/Tabs';
import Button from '@vromo/ui/components/Button';
import { useToasts } from '@vromo/ui/components/Toasts';

import { Upload } from '@vromo/models';
import { CampaignModalContent, TabbedWrapper, TabPanelsWrapper } from './styledComponents';
import Flex from '@vromo/ui/components/Flex';
import General from './panels/General';
import Theme from './panels/Theme';
import Logo from './panels/Logo';
import SMSEditor from './panels/SMSEditor';
import Widgets from './panels/Widgets';
import { qaLabelCampaigns } from './../../../../../common-qa-labels/campaigns';

const LOCALE_GENERAL_LABEL = 'General';
const LOCALE_LOGO_LABEL = 'Logo';
const LOCALE_THEME_LABEL = 'Theme';
const LOCALE_SMS_LABEL = 'SMS Template';
const LOCALE_WIDGETS_LABEL = 'Widgets';

const LOCALE_SAVE = 'Save Campaign';
const LOCALE_CANCEL = 'Cancel';
const LOCALE_EDIT = 'Edit Campaign';

interface Props {
    campaign: Campaign;
    isOpen: boolean;
    onCancel: () => void;
    onSave: () => void;
}

export default observer<Props>(function CampaignEditor({
    campaign: originalCampaign,
    isOpen,
    onCancel,
    onSave
}) {
    const showToast = useToasts();
    const [isSaving, setSaving] = useState<boolean>(false);
    const [logoFile, setLogoFile] = useState<File>();
    const [mapIconFile, setMapIconFile] = useState<File>();

    const [campaign] = useState(deserialize(Campaign, serialize(originalCampaign)) as Campaign);
    const [tab, setTab] = useState('GENERAL');

    const rootStore = useStore<RootStore>('rootStore');

    async function save() {
        setSaving(true);
        const { graph, role } = rootStore.authStore;

        if (logoFile) {
            const response = await graph.root
                .graph()
                .user()
                .uploads()
                .post([{ type: logoFile.type }]);

            await fetch(response.items[0]!.upload, {
                method: 'PUT',
                body: logoFile
            });

            runInAction(() => {
                campaign.logo = new Upload(response.items[0]!.url!);
            });
        }

        if (mapIconFile) {
            const response = await graph.root
                .graph()
                .user()
                .uploads()
                .post([{ type: mapIconFile.type }]);

            await fetch(response.items[0]!.upload, {
                method: 'PUT',
                body: mapIconFile
            });

            runInAction(() => {
                campaign.theme.icon = new Upload(response.items[0]!.url!);
            });
        }

        const serializedCampaign = serialize(campaign);

        delete serializedCampaign.created;

        if (!campaign.theme.icon) serializedCampaign.theme.icon = { url: '' };
        if (!campaign.logo) serializedCampaign.logo = { url: '' };

        try {
            await graph.root
                .graph()
                .roles(role.id)
                .campaigns()
                .post([serializedCampaign])
                .then(
                    action(({ items }) => {
                        update(originalCampaign, serializedCampaign);
                        onSave();
                        showToast(`Successfully updated ${items[0]?.name}`, 'success');
                    })
                );
        } catch (e) {
            console.log('Error saving: ', e);
        } finally {
            setSaving(false);
        }
    }

    function renderTab(tab: string) {
        switch (tab) {
            case 'GENERAL':
                return <General campaign={campaign} />;
            case 'LOGO':
                return <Logo campaign={campaign} onChangeLogo={setLogoFile} />;
            case 'THEME':
                return <Theme campaign={campaign} onChangeMapIcon={setMapIconFile} />;
            case 'SMS_TEMPLATE':
                return <SMSEditor campaign={campaign} />;
            case 'WIDGETS':
                return <Widgets campaign={campaign} />;
        }
    }

    if (!campaign) return null;

    const tabs = [
        { id: 'GENERAL', label: LOCALE_GENERAL_LABEL },
        { id: 'LOGO', label: LOCALE_LOGO_LABEL },
        { id: 'THEME', label: LOCALE_THEME_LABEL },
        { id: 'SMS_TEMPLATE', label: LOCALE_SMS_LABEL },
        { id: 'WIDGETS', label: LOCALE_WIDGETS_LABEL }
    ];

    return (
        <Modal isOpen={isOpen} onRequestClose={onCancel}>
            <ModalHeader>
                <Title data-testid={qaLabelCampaigns.editModal.title}>{LOCALE_EDIT}</Title>
            </ModalHeader>
            <CampaignModalContent>
                <TrackingLinkPreview campaign={campaign} />
                <TabbedWrapper>
                    <Flex>
                        <div style={{ flex: 1 }}>
                            <Tabs currentTab={tab} onChange={setTab}>
                                {tabs.map((it) => (
                                    <Tab key={it.id} id={it.id}>
                                        {it.label}
                                    </Tab>
                                ))}
                            </Tabs>
                            <TabPanelsWrapper>{renderTab(tab)}</TabPanelsWrapper>
                        </div>
                    </Flex>

                    <hr />
                </TabbedWrapper>
            </CampaignModalContent>
            <ModalActions>
                <Button onClick={onCancel}>{LOCALE_CANCEL}</Button>
                <Button
                    data-test-id="save-campaign"
                    primary
                    loading={isSaving}
                    disabled={isSaving}
                    onClick={save}
                    data-testid={qaLabelCampaigns.editCampaignModalSaveButton}>
                    {LOCALE_SAVE}
                </Button>
            </ModalActions>
        </Modal>
    );
});
